/**
 * First-connect bridge — table picker (pure).
 *
 * Chooses which table to land the user on from the table list the app already
 * fetched after connect (no extra engine round-trips). It skips system
 * schemas/tables per engine, prefers a small set of "friendly" business tables
 * (case-insensitive, singular/plural tolerant), and otherwise falls back to the
 * first user table.
 */

import type { DatabaseEngineId } from '../../../core/engines';

/** A candidate table from the already-fetched Object Explorer metadata. */
export type BridgeTableEntry = {
  readonly name: string;
  readonly schema: string;
};

/** How the landing table was chosen (low-cardinality; safe for analytics). */
export type TablePickKind = 'friendly' | 'first' | 'none';

export type TablePickResult =
  | { readonly pick: 'friendly' | 'first'; readonly table: BridgeTableEntry }
  | { readonly pick: 'none'; readonly table: null };

/**
 * Friendly business-table names in priority order. Matched against the
 * singular-normalized table name so both `customers` and `customer` hit.
 */
const FRIENDLY_NAMES = [
  'customers',
  'orders',
  'users',
  'products',
  'accounts',
  'invoices',
  'items',
  'employees',
  'artists',
  'albums',
] as const;

/** Schemas that only ever hold engine internals — never land a new user here. */
const SYSTEM_SCHEMAS: Record<DatabaseEngineId, readonly string[]> = {
  mssql: ['sys', 'information_schema'],
  pgsql: ['pg_catalog', 'information_schema', 'pg_toast'],
  mysql: ['information_schema', 'performance_schema', 'mysql', 'sys'],
  oracle: ['sys', 'system', 'xdb', 'ctxsys', 'mdsys', 'outln', 'dbsnmp', 'appqossys', 'audsys', 'gsmadmin_internal'],
  sqlite: [],
  kusto: [],
  azurestorage: [],
  mock: [],
};

/** Lower-case and drop a single trailing "s" so plurals/singulars unify. */
function normalizeName(name: string): string {
  const lower = name.trim().toLowerCase();
  return lower.endsWith('s') ? lower.slice(0, -1) : lower;
}

const FRIENDLY_NORMALIZED = FRIENDLY_NAMES.map(normalizeName);

function isSystemEntry(engine: DatabaseEngineId, entry: BridgeTableEntry): boolean {
  const schema = entry.schema.trim().toLowerCase();
  if (SYSTEM_SCHEMAS[engine]?.includes(schema)) return true;
  // SQLite keeps its internals in `sqlite_*` tables (no schema layer).
  if (entry.name.trim().toLowerCase().startsWith('sqlite_')) return true;
  return false;
}

/**
 * Pick a landing table. `entries` is the raw table list (system entries
 * included — they are filtered here so the skipping logic stays testable).
 */
/**
 * SQL Server ships these in `master` (and they are `is_ms_shipped`, which the
 * explorer's table listing does not filter on). Belt-and-braces: `collectTables`
 * already refuses a system database as a positive answer, but a user who
 * explicitly connects TO `master` still should not land on `spt_monitor`.
 */
function isMssqlShippedTable(engine: DatabaseEngineId, entry: BridgeTableEntry): boolean {
  if (engine !== 'mssql') return false;
  const name = entry.name.trim().toLowerCase();
  return name.startsWith('spt_') || name === 'msreplication_options';
}

export function pickBridgeTable(
  engine: DatabaseEngineId,
  entries: readonly BridgeTableEntry[]
): TablePickResult {
  const userTables = entries.filter(
    (entry) => entry.name.trim() && !isSystemEntry(engine, entry) && !isMssqlShippedTable(engine, entry),
  );
  if (userTables.length === 0) return { pick: 'none', table: null };

  // Prefer friendly names in their declared priority order.
  for (const wanted of FRIENDLY_NORMALIZED) {
    const match = userTables.find((entry) => normalizeName(entry.name) === wanted);
    if (match) return { pick: 'friendly', table: match };
  }

  return { pick: 'first', table: userTables[0] };
}
